import React, { memo, useCallback } from 'react'
import {
  Box,
  Grid,
  Chip,
  TextField,
  Autocomplete,
  Tooltip,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import { createFilterOptions } from '@mui/material/Autocomplete'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import SettingsIcon from '@mui/icons-material/Settings'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import CourierSelector from './CourierSelector'
import {
  RuleCardContainer,
  StyledAccordion,
  StyledAccordionSummary,
  AccordionContentContainer,
  LeftContentContainer,
  PriorityNumberBox,
  PriorityNumberText,
  RuleInfoGroup,
  RuleNameText,
  RuleMetaRow,
  DeliveryTypeBadge,
  MetaChip,
  ActionsContainer,
  ActionButtonsContainer,
  MoveButton,
  DeleteButton,
  StyledAccordionDetails,
  SectionHeader,
  SectionTitle,
  AllocationCard,
  AllocationCardHeader,
  AllocationCardLabel,
  AllocationCardDescription,
  AdvancedSettingsContainer,
  FallbackCouriersContainer,
  FieldHelperText,
  PincodeManualBox,
} from './styled'
import { AUTO_ALLOCATION_OPTIONS, ZONES, QC_TYPES } from './constants'
import { MaxWeight } from './MaxWeight'
import Pincodes from './Pincodes'

const PACE_OPTION = AUTO_ALLOCATION_OPTIONS[0]

const filter = createFilterOptions()

const RuleCard = memo(
  ({
    rule,
    rules,
    onUpdateRule,
    onDeleteRule,
    onMoveRule,
    courierOptions,
    expandedRule,
    onAccordionChange,
  }) => {
    const { enqueueSnackbar } = useSnackbar()

    const handleUpdateRule = useCallback(
      (updates) => {
        onUpdateRule(rule.id, updates)
      },
      [rule.id, onUpdateRule]
    )

    const handleDeleteRule = useCallback(() => {
      onDeleteRule(rule.id)
    }, [rule.id, onDeleteRule])

    const handleMoveUp = useCallback(() => {
      onMoveRule(rule.id, 'up')
    }, [rule.id, onMoveRule])

    const handleMoveDown = useCallback(() => {
      onMoveRule(rule.id, 'down')
    }, [rule.id, onMoveRule])

    const accentColor = rule.deliveryType === 'FORWARD' ? '#2e7d32' : '#7b1fa2'

    const isExpanded = expandedRule === rule.id

    return (
      <RuleCardContainer isFocused={isExpanded}>
        <StyledAccordion
          expanded={isExpanded}
          onChange={onAccordionChange(rule.id)}
          accentColor={accentColor}
        >
          <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
            <AccordionContentContainer>
              <LeftContentContainer>
                <PriorityNumberBox>
                  <PriorityNumberText>{rule.priority}</PriorityNumberText>
                </PriorityNumberBox>
                <RuleInfoGroup>
                  <RuleNameText>{rule.name}</RuleNameText>
                  <RuleMetaRow>
                    <DeliveryTypeBadge
                      variant={rule.deliveryType}
                      label={rule.deliveryType === 'FORWARD' ? 'Forward' : 'Reverse'}
                      size='small'
                    />
                    <MetaChip label={`Shipment: ${rule.shipmentType}`} size='small' chipValue={rule.shipmentType} />
                    {rule.deliveryType === 'RVP' ? (
                      <MetaChip label={`QC: ${rule.qcType || 'All'}`} size='small' chipValue={rule.qcType} />
                    ) : (
                      <MetaChip label={`Payment: ${rule.paymentMode || 'All'}`} size='small' chipValue={rule.paymentMode} />
                    )}
                    <MetaChip
                      label={rule.dispatchMode ? `Dispatch: ${rule.dispatchMode}` : 'Dispatch: Surface & Air'}
                      size='small'
                      chipValue={rule.dispatchMode || 'Surface & Air'}
                    />
                  </RuleMetaRow>
                </RuleInfoGroup>
              </LeftContentContainer>

              <ActionsContainer>
                <ActionButtonsContainer>
                  <Tooltip title='Move Up'>
                    <MoveButton
                      onClick={(e) => {
                        e.stopPropagation()
                        handleMoveUp()
                      }}
                      disabled={rule.priority === 1}
                    >
                      <KeyboardArrowUpIcon fontSize='small' />
                    </MoveButton>
                  </Tooltip>
                  <Tooltip title='Move Down'>
                    <MoveButton
                      onClick={(e) => {
                        e.stopPropagation()
                        handleMoveDown()
                      }}
                      disabled={rule.priority === rules.length}
                    >
                      <KeyboardArrowDownIcon fontSize='small' />
                    </MoveButton>
                  </Tooltip>
                  <Tooltip title='Delete Rule'>
                    <DeleteButton
                      size='small'
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDeleteRule()
                      }}
                    >
                      <DeleteOutlineIcon fontSize='small' />
                    </DeleteButton>
                  </Tooltip>
                </ActionButtonsContainer>
              </ActionsContainer>
            </AccordionContentContainer>
          </StyledAccordionSummary>

          <StyledAccordionDetails>
            <Grid container spacing={3}>
              {/* Auto Allocation */}
              <Grid item xs={12}>
                <SectionHeader>
                  <AutoAwesomeIcon color='primary' fontSize='small' />
                  <SectionTitle>Auto Allocation (Priority 1)</SectionTitle>
                </SectionHeader>
                {/* PACE is the only automated allocation; legacy codes display as PACE. */}
                <AllocationCard isSelected accentColor={PACE_OPTION.color} sx={{ cursor: 'default' }}>
                  <AllocationCardHeader>
                    <Box sx={{ color: PACE_OPTION.color }}>{PACE_OPTION.icon}</Box>
                    <AllocationCardLabel>{PACE_OPTION.label}</AllocationCardLabel>
                  </AllocationCardHeader>
                  <AllocationCardDescription>{PACE_OPTION.description}</AllocationCardDescription>
                </AllocationCard>
              </Grid>

              {/* Fallback Couriers */}
              <Grid item xs={12}>
                <FallbackCouriersContainer>
                  <SectionHeader>
                    <SectionTitle>Fallback Couriers (Priority 2 &rarr; N)</SectionTitle>
                  </SectionHeader>
                  <CourierSelector
                    options={courierOptions}
                    value={rule.fallbackCouriers || []}
                    onChange={(_event, newValue) =>
                      handleUpdateRule({
                        fallbackCouriers: newValue,
                      })
                    }
                    label='Select Fallback Couriers'
                    placeholder='Search and select couriers...'
                    required
                  />
                  <FieldHelperText>
                    Selected fallback courier partners will apply to all zones that are selected while
                    selecting zones in advanced settings below. Please select carefully.
                  </FieldHelperText>
                </FallbackCouriersContainer>
              </Grid>

              {/* Advanced Settings */}
              <Grid item xs={12}>
                <AdvancedSettingsContainer>
                  <SectionHeader>
                    <SettingsIcon color='primary' fontSize='small' />
                    <SectionTitle>Advanced Settings</SectionTitle>
                  </SectionHeader>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      {rule.shipmentType === 'B2B' && (
                        <TextField
                          label='Zone *'
                          value={rule.advancedSettings.zone}
                          onChange={(e) =>
                            handleUpdateRule({
                              advancedSettings: {
                                ...rule.advancedSettings,
                                zone: e.target.value,
                              },
                            })
                          }
                          fullWidth
                          size='small'
                        />
                      )}
                      {rule.shipmentType === 'B2C' && (
                        <Autocomplete
                          freeSolo
                          options={ZONES}
                          value={rule.advancedSettings.zone || null}
                          onChange={(event, newValue) => {
                            let zoneValue = ''
                            if (typeof newValue === 'string') {
                              zoneValue = newValue
                            } else if (newValue && newValue.inputValue) {
                              zoneValue = newValue.inputValue
                            } else if (newValue) {
                              zoneValue = newValue
                            }
                            handleUpdateRule({
                              advancedSettings: {
                                ...rule.advancedSettings,
                                zone: zoneValue,
                              },
                            })
                          }}
                          filterOptions={(options, params) => {
                            const filtered = filter(options, params)
                            const { inputValue } = params
                            const isExisting = options.some((option) => inputValue === option)
                            if (inputValue !== '' && !isExisting) {
                              filtered.push({
                                inputValue,
                                title: `Add "${inputValue}"`,
                              })
                            }
                            return filtered
                          }}
                          getOptionLabel={(option) => {
                            if (typeof option === 'string') {
                              return option
                            }
                            if (option.inputValue) {
                              return option.inputValue
                            }
                            return option.title || option
                          }}
                          selectOnFocus
                          clearOnBlur
                          handleHomeEndKeys
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label='Zone *'
                              placeholder='Select from options or enter custom zone...'
                              size='small'
                            />
                          )}
                        />
                      )}
                      <FieldHelperText>
                        Select from predefined zones or enter a custom zone name
                      </FieldHelperText>
                    </Grid>

                    {rule.deliveryType === 'RVP' && (
                      <Grid item xs={6}>
                        <FormControl fullWidth size='small'>
                          <InputLabel>QC Type *</InputLabel>
                          <Select
                            value={rule.qcType || ''}
                            onChange={(e) =>
                              handleUpdateRule({
                                qcType: e.target.value,
                              })
                            }
                            label='QC Type *'
                            required
                          >
                            {QC_TYPES.map((type) => (
                              <MenuItem key={type} value={type}>
                                {type}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FieldHelperText>
                          QC: Quality Check enabled (doorstep couriers) | Non-QC: Quality Check not
                          enabled
                        </FieldHelperText>
                      </Grid>
                    )}

                    <Grid item xs={3}>
                      <TextField
                        required
                        label='Min Weight (kg)'
                        type='number'
                        value={rule.advancedSettings.minWeight}
                        onChange={(e) => {
                          const value = e.target.value
                          if (value === '' || /^\d+$/.test(value) || /^\d+\.\d{1,2}$/.test(value)) {
                            handleUpdateRule({
                              advancedSettings: {
                                ...rule.advancedSettings,
                                minWeight: value,
                              },
                            })
                          }
                        }}
                        inputProps={{
                          step: '0.1',
                          min: '0',
                        }}
                        fullWidth
                        size='small'
                      />
                      <FieldHelperText>
                        Minimum weight in kilograms for this rule to apply
                      </FieldHelperText>
                    </Grid>

                    <Grid item xs={3}>
                      <MaxWeight rule={rule} handleUpdateRule={handleUpdateRule} />
                      <FieldHelperText>
                        Maximum weight in kilograms for this rule to apply
                        {rule.shipmentType === 'B2C' && ' (Max: 50 kg)'}
                      </FieldHelperText>
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        label='Min Invoice Value (INR)'
                        type='number'
                        value={rule.advancedSettings.minInvoice}
                        onChange={(e) => {
                          const value = e.target.value
                          if (value === '' || /^\d+$/.test(value)) {
                            const numValue = value === '' ? '' : parseInt(value, 10)
                            if (numValue !== '' && numValue < 1) {
                              enqueueSnackbar('Min invoice value must be at least ₹1.', {
                                variant: 'error',
                              })
                              return
                            }
                            if (value === '' || numValue >= 1) {
                              handleUpdateRule({
                                advancedSettings: {
                                  ...rule.advancedSettings,
                                  minInvoice: value,
                                },
                              })
                            }
                          }
                        }}
                        inputProps={{ min: '1' }}
                        fullWidth
                        size='small'
                      />
                      <FieldHelperText>
                        Minimum invoice value in INR for this rule to apply (Min: ₹1)
                      </FieldHelperText>
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        label='Max Invoice Value (INR)'
                        type='number'
                        value={rule.advancedSettings.maxInvoice}
                        onChange={(e) => {
                          const value = e.target.value
                          if (value === '' || /^\d+$/.test(value)) {
                            const numValue = value === '' ? '' : parseInt(value, 10)
                            if (rule.shipmentType === 'B2C' && numValue !== '' && numValue > 49999) {
                              enqueueSnackbar('Max invoice value cannot exceed ₹49,999.', {
                                variant: 'error',
                              })
                              return
                            }
                            if (value === '' || numValue >= 0) {
                              handleUpdateRule({
                                advancedSettings: {
                                  ...rule.advancedSettings,
                                  maxInvoice: value,
                                },
                              })
                            }
                          }
                        }}
                        inputProps={{ min: '0', max: '49999' }}
                        fullWidth
                        size='small'
                      />
                      <FieldHelperText>
                        Maximum invoice value in INR for this rule to apply (Max: ₹49,999)
                      </FieldHelperText>
                    </Grid>

                    <Grid item xs={6}>
                      <Pincodes
                        label='Pickup Pincode'
                        value={rule.servicablePickupPincodes}
                        onChange={(e) => handleUpdateRule({ ...rule.advancedSettings, servicablePickupPincodes: e.target.value })}
                      />
                      {rule.servicablePickupPincodes === 'MANUAL' && (
                        <PincodeManualBox>
                          <Autocomplete
                            multiple
                            freeSolo
                            options={[]}
                            value={
                              Array.isArray(rule.advancedSettings.pickupPincode)
                                ? rule.advancedSettings.pickupPincode
                                : []
                            }
                            onChange={(event, newValue) => {
                              const numericValues = newValue
                                .map((val) => String(val).trim())
                                .filter((val) => /^\d{6}$/.test(val))
                              handleUpdateRule({
                                advancedSettings: {
                                  ...rule.advancedSettings,
                                  pickupPincode: numericValues,
                                },
                              })
                            }}
                            renderTags={(value, getTagProps) =>
                              value.map((option, index) => (
                                <Chip
                                  color='primary'
                                  label={option}
                                  size='small'
                                  {...getTagProps({ index })}
                                  key={option}
                                />
                              ))
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label='Enter Pickup Pincode Manually'
                                placeholder='Enter pincodes (numbers only) for specific pincodes...'
                                size='small'
                                inputProps={{
                                  ...params.inputProps,
                                  pattern: '[0-9]*',
                                  inputMode: 'numeric',
                                }}
                              />
                            )}
                            onKeyPress={(event) => {
                              const allowedKeys = /[0-9]|Enter|Comma|Backspace|Delete|Tab/
                              if (!allowedKeys.test(event.key)) {
                                event.preventDefault()
                              }
                            }}
                          />
                          <FieldHelperText>
                            Enter pickup pincodes (numbers only). Press Enter to add multiple pincodes
                          </FieldHelperText>
                        </PincodeManualBox>
                      )}
                    </Grid>

                    <Grid item xs={6}>
                      <Pincodes
                        label='Drop Pincode'
                        value={rule.servicableDropPincodes}
                        onChange={(e) => handleUpdateRule({ ...rule.advancedSettings, servicableDropPincodes: e.target.value })}
                      />
                      {rule.servicableDropPincodes === 'MANUAL' && (
                        <PincodeManualBox>
                          <Autocomplete
                            multiple
                            freeSolo
                            options={[]}
                            value={
                              Array.isArray(rule.advancedSettings.dropPincode)
                                ? rule.advancedSettings.dropPincode
                                : []
                            }
                            onChange={(event, newValue) => {
                              const numericValues = newValue
                                .map((val) => String(val).trim())
                                .filter((val) => /^\d{6}$/.test(val))
                              handleUpdateRule({
                                advancedSettings: {
                                  ...rule.advancedSettings,
                                  dropPincode: numericValues,
                                },
                              })
                            }}
                            renderTags={(value, getTagProps) =>
                              value.map((option, index) => (
                                <Chip
                                  color='primary'
                                  label={option}
                                  size='small'
                                  {...getTagProps({ index })}
                                  key={option}
                                />
                              ))
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label='Enter Drop Pincode Manually'
                                placeholder='Enter pincodes (numbers only) for specific pincodes...'
                                size='small'
                                inputProps={{
                                  ...params.inputProps,
                                  pattern: '[0-9]*',
                                  inputMode: 'numeric',
                                }}
                              />
                            )}
                            onKeyPress={(event) => {
                              const allowedKeys = /[0-9]|Enter|Comma|Backspace|Delete|Tab/
                              if (!allowedKeys.test(event.key)) {
                                event.preventDefault()
                              }
                            }}
                          />
                          <FieldHelperText>
                            Enter delivery pincodes (numbers only). Press Enter to add multiple pincodes
                          </FieldHelperText>
                        </PincodeManualBox>
                      )}
                    </Grid>

                    <Grid item xs={6}>
                      <Autocomplete
                        multiple
                        freeSolo
                        options={[]}
                        value={
                          Array.isArray(rule.advancedSettings.channel)
                            ? rule.advancedSettings.channel
                            : []
                        }
                        onChange={(event, newValue) => {
                          handleUpdateRule({
                            advancedSettings: {
                              ...rule.advancedSettings,
                              channel: newValue
                                .map((val) => String(val).trim())
                                .filter((val) => val !== ''),
                            },
                          })
                        }}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              color='primary'
                              label={option}
                              size='small'
                              {...getTagProps({ index })}
                              key={option}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label='Channel'
                            placeholder='Enter channels (e.g., Amazon, Flipkart)...'
                            size='small'
                          />
                        )}
                      />
                      <FieldHelperText>
                        Sales channel filter (Amazon, Website, Myntra, Flipkart, etc.). Press Enter to
                        add multiple channels
                      </FieldHelperText>
                    </Grid>

                    <Grid item xs={6} hidden={rule.deliveryType !== 'RVP'}>
                      <TextField
                        label='RVP Reason'
                        value={rule.advancedSettings.rvpReason}
                        onChange={(e) =>
                          handleUpdateRule({
                            advancedSettings: {
                              ...rule.advancedSettings,
                              rvpReason: e.target.value,
                            },
                          })
                        }
                        fullWidth
                        size='small'
                      />
                      <FieldHelperText>
                        Reason for reverse pickup (e.g., Quality Check, Return, Exchange)
                      </FieldHelperText>
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        label='Item Units'
                        type='number'
                        value={rule.advancedSettings.itemUnits}
                        onChange={(e) => {
                          const value = e.target.value
                          if (value === '' || /^\d+$/.test(value)) {
                            const numValue = value === '' ? '' : parseInt(value, 10)
                            if (value === '' || numValue > 0) {
                              handleUpdateRule({
                                advancedSettings: {
                                  ...rule.advancedSettings,
                                  itemUnits: value,
                                },
                              })
                            }
                          }
                        }}
                        inputProps={{ min: '1' }}
                        fullWidth
                        size='small'
                      />
                      <FieldHelperText>
                        Number of item units for this rule to apply
                      </FieldHelperText>
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        label='Unique SKU Count'
                        type='number'
                        value={rule.advancedSettings.uniqueSkuCount}
                        onChange={(e) => {
                          const value = e.target.value
                          if (value === '' || /^\d+$/.test(value)) {
                            const numValue = value === '' ? '' : parseInt(value, 10)
                            if (value === '' || numValue > 0) {
                              handleUpdateRule({
                                advancedSettings: {
                                  ...rule.advancedSettings,
                                  uniqueSkuCount: value,
                                },
                              })
                            }
                          }
                        }}
                        inputProps={{ min: '1' }}
                        fullWidth
                        size='small'
                      />
                      <FieldHelperText>
                        Number of unique SKUs for this rule to apply
                      </FieldHelperText>
                    </Grid>
                  </Grid>
                </AdvancedSettingsContainer>
              </Grid>
            </Grid>
          </StyledAccordionDetails>
        </StyledAccordion>
      </RuleCardContainer>
    )
  }
)

RuleCard.displayName = 'RuleCard'

export default RuleCard
